import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import { authService } from '../../utils/authService';
import {
  Button, Card, EmptyState, KpiCard, Note, Select, SkeletonPage, Tabs,
  useCompany, usePageTitle,
} from '../../components/app2';
import SyncRow from './components/SyncRow';
import { ESTADOS } from './components/EstadoSync';
import CoberturaPanel from './components/CoberturaPanel';

/**
 * Sincronizaciones (jul-2026).
 *
 * Dos vistas del mismo hecho: lo que está corriendo AHORA y todo lo que ha
 * pasado. Existe porque el ciclo dejó de depender del navegador: como el
 * servidor avanza las solicitudes solo, hace falta un sitio donde verlas — y un
 * histórico que sea auditable, no un recuerdo del navegador.
 *
 * El monitor se refresca cada 5 s mientras hay algo vivo. No usa WebSocket a
 * propósito: el usuario no necesita el avance al segundo, necesita que el avance
 * OCURRA aunque no mire; un sondeo ligero cumple sin agregar una pieza nueva al
 * stack.
 */

const REFRESCO_MS = 5000;

const SatSyncScreen = () => {
  const navigate = useNavigate();
  const { empresas, idEmpresaActiva } = useCompany();
  const rol = authService?.getCurrentUser()?.role;
  const esSuper = rol === 'administrator';

  const [pestana, setPestana] = useState('curso');

  // Cobertura: el año en curso por defecto, que es lo que se mira el 99 % de las
  // veces. La empresa sale de la activa (o del filtro del super admin).
  const anioActual = new Date().getFullYear();
  const [anioCobertura, setAnioCobertura] = useState(anioActual);
  // Empresa propia de la pestaña, NO la del filtro del histórico: si se
  // compartieran, el desplegable diría "Elige una empresa" mientras el mapa
  // sigue mostrando la activa, y lo que se lee no sería lo que se ve.
  const [empresaCobertura, setEmpresaCobertura] = useState(idEmpresaActiva || '');
  const [cobertura, setCobertura] = useState(null);
  const [cargandoCobertura, setCargandoCobertura] = useState(false);
  const [enCurso, setEnCurso] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [totalHistorico, setTotalHistorico] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(null);

  // Filtros del histórico
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const [pagina, setPagina] = useState(1);

  const temporizador = useRef(null);

  usePageTitle({ title: 'Sincronizaciones', kicker: 'Operación' });

  const cargarEnCurso = useCallback(async () => {
    try {
      const r = await strapiService.satSync.enCurso(esSuper ? undefined : idEmpresaActiva);
      setEnCurso(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      console.error('Error consultando lo que está en curso:', e);
    }
  }, [esSuper, idEmpresaActiva]);

  const cargarHistorico = useCallback(async () => {
    try {
      const r = await strapiService.satSync.historico({
        companyId: filtroEmpresa || undefined,
        status: filtroEstado || undefined,
        origin: filtroOrigen || undefined,
        page: pagina,
        limit: 25,
      });
      setHistorico(Array.isArray(r?.data) ? r.data : []);
      setTotalHistorico(r?.meta?.total || 0);
    } catch (e) {
      console.error('Error cargando el histórico:', e);
    }
  }, [filtroEmpresa, filtroEstado, filtroOrigen, pagina]);

  // Primera carga
  useEffect(() => {
    (async () => {
      setCargando(true);
      await Promise.all([cargarEnCurso(), cargarHistorico()]);
      setCargando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargarHistorico(); }, [cargarHistorico]);

  /**
   * Refresco del monitor. Solo insiste mientras hay algo vivo: cuando todo
   * terminó, deja de preguntar (una pantalla abierta toda la noche no debe
   * golpear el servidor sin motivo).
   */
  useEffect(() => {
    clearInterval(temporizador.current);
    if (pestana !== 'curso') return undefined;

    temporizador.current = setInterval(async () => {
      await cargarEnCurso();
    }, REFRESCO_MS);

    return () => clearInterval(temporizador.current);
  }, [pestana, cargarEnCurso, enCurso.length]);

  const cancelar = async (solicitud) => {
    const seguro = await AlertService.confirm(
      'Cancelar la sincronización',
      'Aún no se ha pedido al SAT, así que se puede cancelar sin consecuencias. ¿Cancelarla?',
      { confirmText: 'Sí, cancelar', cancelText: 'Dejarla' },
    );
    if (!seguro) return;

    try {
      setOcupado(solicitud.id);
      await strapiService.satSync.cancelar(solicitud.id);
      await Promise.all([cargarEnCurso(), cargarHistorico()]);
    } catch (e) {
      AlertService.error('No se pudo cancelar', e?.response?.data?.error?.message || e.message);
    } finally {
      setOcupado(null);
    }
  };

  const reintentar = async (solicitud) => {
    try {
      setOcupado(solicitud.id);
      const r = await strapiService.satSync.reintentar(solicitud.id);
      if (r?.success) {
        AlertService.success('Sincronización encolada', 'Se pidió de nuevo el mismo periodo. Puedes cerrar esta pantalla: el servidor la lleva sola.');
        setPestana('curso');
        await Promise.all([cargarEnCurso(), cargarHistorico()]);
      }
    } catch (e) {
      AlertService.error('No se pudo reintentar', e?.response?.data?.error?.message || e.message);
    } finally {
      setOcupado(null);
    }
  };

  // Al cambiar la empresa activa desde el encabezado, el mapa la sigue.
  useEffect(() => {
    if (idEmpresaActiva) setEmpresaCobertura(idEmpresaActiva);
  }, [idEmpresaActiva]);

  const cargarCobertura = useCallback(async () => {
    if (!empresaCobertura) { setCobertura(null); return; }
    setCargandoCobertura(true);
    try {
      const r = await strapiService.satSync.cobertura({
        companyId: empresaCobertura,
        from: `${anioCobertura}-01-01`,
        to: `${anioCobertura}-12-31`,
      });
      setCobertura(r || null);
    } catch (e) {
      console.error('Error cargando la cobertura:', e);
      setCobertura(null);
    } finally {
      setCargandoCobertura(false);
    }
  }, [empresaCobertura, anioCobertura]);

  useEffect(() => {
    if (pestana === 'cobertura') cargarCobertura();
  }, [pestana, cargarCobertura]);

  const resumen = useMemo(() => ({
    vivas: enCurso.length,
    total: totalHistorico,
  }), [enCurso.length, totalHistorico]);

  if (cargando) return <SkeletonPage kpis={2} rows={6} />;

  return (
    <div className="a2-anim-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <div className="a2-grid-kpi">
        <KpiCard icon="autorenew" label="En curso ahora" value={resumen.vivas} />
        <KpiCard icon="history" label="Sincronizaciones registradas" value={resumen.total.toLocaleString('es-MX')} />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Note tone="info" icon="dns" style={{ flex: '1 1 320px' }}>
          <span>
            <strong style={{ color: 'var(--heading)' }}>Las sincronizaciones corren en el servidor.</strong>{' '}
            Puedes cerrar esta pantalla: el proceso sigue solo y aquí queda el registro de lo que pasó.
          </span>
        </Note>
        {esSuper ? (
          <Button icon="groups" onClick={() => navigate('/app/sync/masiva')} style={{ flex: '0 0 auto' }}>
            Sincronizar varias empresas
          </Button>
        ) : null}
      </div>

      <Card pad={false} style={{ padding: 'clamp(18px,2.5vw,24px)' }}>
        <Tabs
          value={pestana}
          onChange={setPestana}
          items={[
            { id: 'curso', label: `En curso${resumen.vivas ? ` (${resumen.vivas})` : ''}` },
            { id: 'historico', label: 'Histórico' },
            { id: 'cobertura', label: 'Cobertura' },
          ]}
          style={{ marginBottom: 18 }}
        />

        {pestana === 'cobertura' ? (
          <CoberturaPanel
            empresas={empresas}
            esSuper={esSuper}
            empresaId={empresaCobertura}
            onCambiarEmpresa={(id) => setEmpresaCobertura(id)}
            anio={anioCobertura}
            onCambiarAnio={setAnioCobertura}
            cobertura={cobertura}
            cargando={cargandoCobertura}
            onAbrirSolicitud={(d) => navigate(`/app/sync/${d.solicitud_id}`)}
          />
        ) : pestana === 'curso' ? (
          enCurso.length === 0 ? (
            <EmptyState
              icon="cloud_done"
              title="No hay nada en curso"
              text="Cuando inicies una sincronización aparecerá aquí y podrás seguir su avance, aunque cierres la pantalla."
            />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }}>
              {enCurso.map((s) => (
                <SyncRow key={s.id} solicitud={s} onCancelar={cancelar} ocupado={ocupado === s.id}
                  onVerDetalle={(x) => navigate(`/app/sync/${x.id}`)} />
              ))}
            </div>
          )
        ) : (
          <div>
            {/* Filtros del histórico */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
              {esSuper ? (
                <Select
                  value={filtroEmpresa}
                  onChange={(e) => { setFiltroEmpresa(e?.target?.value); setPagina(1); }}
                  aria-label="Filtrar por empresa"
                  style={{ width: 'auto', minWidth: 210 }}
                >
                  <option value="">Todas las empresas</option>
                  {empresas.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.businessName}</option>
                  ))}
                </Select>
              ) : null}

              <Select
                value={filtroEstado}
                onChange={(e) => { setFiltroEstado(e?.target?.value); setPagina(1); }}
                aria-label="Filtrar por estado"
                style={{ width: 'auto', minWidth: 175 }}
              >
                <option value="">Todos los estados</option>
                {Object.entries(ESTADOS).map(([valor, e]) => (
                  <option key={valor} value={valor}>{e.label}</option>
                ))}
              </Select>

              <Select
                value={filtroOrigen}
                onChange={(e) => { setFiltroOrigen(e?.target?.value); setPagina(1); }}
                aria-label="Filtrar por origen"
                style={{ width: 'auto', minWidth: 160 }}
              >
                <option value="">Todos los orígenes</option>
                <option value="manual">Manual</option>
                <option value="programada">Automática</option>
                <option value="masiva">Masiva</option>
              </Select>
            </div>

            {historico.length === 0 ? (
              <EmptyState
                icon="history"
                title="Sin sincronizaciones"
                text="Aquí queda el registro de cada sincronización: cuándo se pidió, qué periodo, cuánto tardó y qué trajo."
              />
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }}>
                  {historico.map((s) => (
                    <SyncRow key={s.id} solicitud={s} onReintentar={reintentar} ocupado={ocupado === s.id}
                      onVerDetalle={(x) => navigate(`/app/sync/${x.id}`)} />
                  ))}
                </div>

                {totalHistorico > 25 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 16 }}>
                    <span className="a2-tcell-muted">
                      {(pagina - 1) * 25 + 1}–{Math.min(pagina * 25, totalHistorico)} de {totalHistorico}
                    </span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <Button variant="outline" size="sm" icon="chevron_left"
                        disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}
                        aria-label="Página anterior"
                      >
                        Anterior
                      </Button>
                      <Button variant="outline" size="sm" iconRight="chevron_right"
                        disabled={pagina * 25 >= totalHistorico} onClick={() => setPagina((p) => p + 1)}
                        aria-label="Página siguiente"
                      >
                        Siguiente
                      </Button>
                    </span>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SatSyncScreen;
